import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Save } from 'lucide-react';
import {
  fieldClass,
  labelClass,
  primaryBtnClass,
  secondaryBtnClass,
} from '../styles';
import {
  createCategoryType,
  fetchCategoryTypes,
  updateCategoryType,
} from './api';
import {
  CategoryTypeFormValue,
  CategoryTypeRow,
  emptyCategoryTypeForm,
  formFromCategoryType,
} from './types';

function normalizeHex(value: string): string {
  const raw = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(raw)) return raw.toUpperCase();
  if (/^[0-9A-Fa-f]{6}$/.test(raw)) return `#${raw.toUpperCase()}`;
  return raw;
}

const CategoryTypesPanel: React.FC = () => {
  const [rows, setRows] = useState<CategoryTypeRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<CategoryTypeFormValue>(emptyCategoryTypeForm());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCategoryTypes();
      setRows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load category types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (selectedId === 'new') {
      setForm(emptyCategoryTypeForm());
      return;
    }
    if (selectedId == null) return;
    const row = rows.find((r) => r.id === selectedId);
    if (row) setForm(formFromCategoryType(row));
  }, [selectedId, rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const save = async () => {
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    const color = normalizeHex(form.color);
    if (color && !/^#[0-9A-Fa-f]{6}$/.test(color)) {
      setError('Color must be a hex code like #C3D74F.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = { ...form, color: color || '#C3D74F' };
      if (selectedId === 'new') {
        const created = await createCategoryType(payload);
        setRows((prev) => [...prev, created].sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)));
        setSelectedId(created.id);
        setMessage('Category type created.');
      } else if (typeof selectedId === 'number') {
        const updated = await updateCategoryType(selectedId, payload);
        setRows((prev) =>
          prev
            .map((r) => (r.id === updated.id ? updated : r))
            .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
        );
        setMessage('Category type saved.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading category types…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(error || message) && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || message}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[520px]">
        <div className="lg:col-span-4 border border-[#E8E8E8] rounded-lg overflow-hidden flex flex-col">
          <div className="px-3 py-2 border-b border-[#E8E8E8] flex items-center gap-2">
            <input
              className={`${fieldClass} h-9`}
              placeholder="Search types…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              type="button"
              className={`${secondaryBtnClass} shrink-0 inline-flex items-center gap-1`}
              onClick={() => {
                setSelectedId('new');
                setMessage(null);
                setError(null);
              }}
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          </div>
          <ul className="flex-1 max-h-[480px] overflow-y-auto">
            {filtered.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(row.id);
                    setMessage(null);
                    setError(null);
                  }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm border-b border-[#F0F0F0] ${
                    selectedId === row.id
                      ? 'bg-[#F3F8FC] text-[#0C0B0B] font-medium'
                      : 'text-[#4A4A4A] hover:bg-[#FAFAFA]'
                  }`}
                >
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ background: row.color || '#C3D74F' }}
                    />
                    <span className="truncate">{row.name}</span>
                    {!row.is_active && (
                      <span className="text-[10px] uppercase tracking-wide text-[#8A8A8A]">
                        inactive
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] text-[#8A8A8A] shrink-0">#{row.display_order}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-sm text-[#8A8A8A]">No category types</li>
            )}
          </ul>
        </div>

        <div className="lg:col-span-8 border border-[#E8E8E8] rounded-lg p-4">
          {selectedId == null ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-sm text-[#8A8A8A] border border-dashed border-[#E0E0E0] rounded-lg">
              Select a category type or click Add.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-[#0C0B0B]">
                  {selectedId === 'new' ? 'New category type' : 'Edit category type'}
                </h3>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save()}
                  className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
                >
                  <Save className="w-3.5 h-3.5" />
                  Save
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={labelClass}>Name</label>
                  <input
                    className={fieldClass}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Description</label>
                  <textarea
                    className={`${fieldClass} min-h-[88px]`}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="h-10 w-12 rounded border border-[#D1D1D1] bg-white p-1 cursor-pointer"
                      value={/^#[0-9A-Fa-f]{6}$/.test(form.color) ? form.color : '#C3D74F'}
                      onChange={(e) =>
                        setForm({ ...form, color: e.target.value.toUpperCase() })
                      }
                      aria-label="Color picker"
                    />
                    <input
                      className={fieldClass}
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      onBlur={() =>
                        setForm((prev) => ({ ...prev, color: normalizeHex(prev.color) || prev.color }))
                      }
                      placeholder="#C3D74F"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Display order</label>
                  <input
                    type="number"
                    className={fieldClass}
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2 pt-1">
                  <label className="inline-flex items-center gap-2 text-sm text-[#4A4A4A]">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryTypesPanel;
